import { useEffect, useState } from "react";
import { Rss } from "lucide-react";

const WEATHER: [number, string][] = [[0, "Ensoleillé"], [3, "Partiellement nuageux"], [48, "Brouillard"], [67, "Pluie"], [77, "Neige"], [82, "Averses"], [86, "Averses de neige"], [99, "Orages"]];
export const weatherLabel = (code: number) => (WEATHER.find(([max]) => code <= max) || WEATHER[WEATHER.length - 1])[1];

export function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

export function AnalogClock({ date, size = 120, tz }: { date: Date; size?: number; tz?: string }) {
  const parts = new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "numeric", second: "numeric", hour12: false, timeZone: tz }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value || 0);
  const h = get("hour") % 12, m = get("minute"), s = get("second");
  const hand = (deg: number, len: number, w: number, color: string) => <line x1="50" y1="50" x2={50 + len * Math.sin((deg * Math.PI) / 180)} y2={50 - len * Math.cos((deg * Math.PI) / 180)} stroke={color} strokeWidth={w} strokeLinecap="round" />;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} data-testid="analog-clock">
      <circle cx="50" cy="50" r="47" fill="rgba(0,0,0,0.55)" stroke="#f97316" strokeWidth="3" />
      {Array.from({ length: 12 }, (_, i) => <line key={i} x1="50" y1="8" x2="50" y2="14" stroke="#cbd5e1" strokeWidth="2" transform={`rotate(${i * 30} 50 50)`} />)}
      {hand(h * 30 + m / 2, 24, 4, "#fff")}{hand(m * 6, 34, 3, "#fff")}{hand(s * 6, 38, 1, "#f97316")}
    </svg>
  );
}

export function formatClock(date: Date, style: string, tz?: string) {
  return date.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit", hour12: style === "digital_12h", timeZone: tz });
}

export function ClockSlide({ options }: { options: Record<string, any> }) {
  const now = useNow();
  return (
    <div data-testid="clock-slide" className="flex h-full flex-col items-center justify-center gap-3 text-white">
      {options.style === "analog" ? <AnalogClock date={now} size={180} tz={options.timezone} /> : <p className="font-mono text-6xl font-semibold">{formatClock(now, options.style, options.timezone)}</p>}
      <p className="text-sm uppercase tracking-[0.2em] text-slate-300">{options.label || options.timezone}</p>
    </div>
  );
}

export function WeatherSlide({ url, options }: { url: string; options: Record<string, any> }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => { fetch(url).then((r) => r.json()).then(setData).catch(() => null); }, [url]);
  const unit = options.units === "imperial" ? "°F" : "°C";
  return (
    <div data-testid="weather-slide" className="flex h-full flex-col items-center justify-center gap-2 text-white">
      <p className="text-sm uppercase tracking-[0.2em] text-slate-300">{options.location}</p>
      {data?.current ? (
        <>
          <p className="font-display text-6xl font-semibold">{Math.round(data.current.temperature_2m)}{unit}</p>
          <p className="text-lg text-orange-300">{weatherLabel(data.current.weather_code)}</p>
          <div className="mt-3 flex gap-6 text-xs text-slate-300">
            {(data.daily?.time || []).map((d: string, i: number) => (
              <span key={d} className="text-center">{new Date(d + "T12:00").toLocaleDateString("fr-CA", { weekday: "short" })}<br />{Math.round(data.daily.temperature_2m_max[i])}° / {Math.round(data.daily.temperature_2m_min[i])}°</span>
            ))}
          </div>
        </>
      ) : <p className="text-sm text-slate-400">Chargement de la météo…</p>}
    </div>
  );
}

export function RssSlide({ url }: { url: string }) {
  return (
    <div data-testid="rss-slide" className="flex h-full flex-col items-center justify-center gap-2 text-slate-300">
      <Rss className="h-10 w-10 text-orange-400" />
      <p className="text-sm">Flux RSS : les derniers titres défilent sur la télé</p>
      <p className="max-w-[80%] truncate font-mono text-xs text-slate-500">{url}</p>
    </div>
  );
}
